import type { StoreRegion } from "@medusajs/types";
import {
  queryOptions,
  type UseQueryResult,
  useQuery,
} from "@tanstack/react-query";
import { medusa } from "#/lib/medusa";

export const regionKeys = {
  all: ["storefront", "regions"] as const,
  list: ["storefront", "regions", "list"] as const,
  default: ["storefront", "regions", "default"] as const,
};

export async function fetchDefaultRegion(): Promise<StoreRegion | null> {
  const { regions } = await medusa.store.region.list();
  const preferred = import.meta.env.MEDUSA_DEFAULT_CURRENCY?.toLowerCase();
  if (preferred) {
    const match = regions.find((r) => r.currency_code === preferred);
    if (match) return match;
  }
  return regions[0] ?? null;
}

export const regionQueries = {
  list: () =>
    queryOptions({
      queryKey: regionKeys.list,
      queryFn: async (): Promise<StoreRegion[]> => {
        const { regions } = await medusa.store.region.list();
        return regions;
      },
      staleTime: Number.POSITIVE_INFINITY,
    }),
  default: () =>
    queryOptions({
      queryKey: regionKeys.default,
      queryFn: fetchDefaultRegion,
      staleTime: Number.POSITIVE_INFINITY,
    }),
};

export function useRegions(): UseQueryResult<StoreRegion[]> {
  return useQuery(regionQueries.list());
}

export function useDefaultRegion(): UseQueryResult<StoreRegion | null> {
  return useQuery(regionQueries.default());
}

export function useDefaultRegionId(): string | undefined {
  return useDefaultRegion().data?.id;
}
